import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { CreateDeviceResponse } from '../../types/CreateDeviceResponse';
import { MfaDevice } from '../../types/MfaDevice';
import { post } from '../../utils/communication/api';
import { PollRequest } from '../../utils/communication/poll-request';
import { i18n } from '../../utils/i18n/i18n';

@customElement('as-mfa-device-qr-code')
export class MfaDeviceQrCode extends LitElement {
  public static styles = css`
    .qr-code {
      margin-block: 1rem;
      margin-inline: auto;
      max-width: 200px;
    }

    .debug-mode {
      margin-block: 1rem;
      text-align: center;
    }

    .link {
      text-align: center;
      a {
        color: #e2001a;
      }
    }
  `;

  @property({ type: Boolean })
  public debugMode = false;

  @property()
  public successCallbackUrl: string | undefined = undefined;

  @property()
  public errorCallbackUrl: string | undefined = undefined;

  @state()
  private activationUrl: string | undefined = undefined;

  public render() {
    if (!this.activationUrl) {
      return undefined;
    }

    return html`
      <div class="qr-code">
        <sd-qrcode data="${this.activationUrl}" margin="0"></sd-qrcode>
      </div>

      ${this.debugMode
        ? html`<div class="debug-mode">
            <sd-button primary @click="${this.copyActivationUrl}">Copy to clipboard</sd-button>
          </div>`
        : undefined}
      ${this.isMobileView()
        ? html` <div class="link">
            ${i18n('mfa.ui.registerdevice.notscanable')}
            <a id="alternative-link" href=${this.activationUrl}>${i18n('mfa.ui.registerdevice.alternativelink')}</a>
          </div>`
        : undefined}
    `;
  }

  public connectedCallback() {
    super.connectedCallback();
    this.requestActivationUrl();
  }

  private copyActivationUrl() {
    if (this.activationUrl) {
      navigator.clipboard.writeText(this.activationUrl);
    }
  }

  private async requestActivationUrl() {
    const createDeviceResponse = await post<CreateDeviceResponse, unknown>(
      '/api-internal/mfa/devices/register',
      null,
      true
    );

    this.activationUrl = createDeviceResponse.url;

    const pollRequest = new PollRequest<MfaDevice>(`/api-internal/mfa/devices/${createDeviceResponse.deviceId}`, 3000);

    pollRequest.startPolling((response, status, next) => {
      if (status !== 200) {
        this.dispatchEvent(new CustomEvent('error'));

        if (this.errorCallbackUrl) {
          window.location.replace(this.errorCallbackUrl);
        }

        return;
      }

      if (response.verified) {
        this.dispatchEvent(new CustomEvent('success'));

        if (this.successCallbackUrl) {
          window.location.replace(this.successCallbackUrl);
        }

        return;
      }

      next();
    });
  }

  private isMobileView(): boolean {
    return window.innerWidth < 768;
  }
}
